package org.catharsis.spendtel.feature.presentation.web

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.AutoAwesome
import androidx.compose.material.icons.outlined.CompareArrows
import androidx.compose.material.icons.outlined.Flag
import androidx.compose.material.icons.outlined.Lock
import androidx.compose.material.icons.outlined.Psychology
import androidx.compose.material.icons.outlined.ReceiptLong
import androidx.compose.material.icons.outlined.ShowChart
import androidx.compose.material.icons.outlined.Storefront
import androidx.compose.material.icons.outlined.UploadFile
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.navigation.NavHostController
import org.catharsis.TVTally.theme.deepGreen
import org.catharsis.spendtel.core.navigation.AppRoutes
import org.catharsis.spendtel.feature.legal.LegalConstants
import org.catharsis.spendtel.feature.presentation.expensereport.SpendTelGradients
import org.catharsis.spendtel.feature.presentation.expensereport.SpendTelReportColors
import org.catharsis.spendtel.ui.LegalLinks
import org.catharsis.spendtel.ui.components.PlatformScrollableColumn

private val pageMaxWidth = 1140.dp
private val sectionPadding = 24.dp

@Composable
fun WebLandingScreen(navHostController: NavHostController) {
    val scrollState = rememberScrollState()

    fun openApp() {
        navHostController.navigate(AppRoutes.Authentication.name)
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(SpendTelReportColors.canvas),
    ) {
        PlatformScrollableColumn(
            modifier = Modifier.fillMaxSize(),
            scrollState = scrollState,
        ) {
            LandingTopNav(onSignIn = ::openApp, onGetStarted = ::openApp)

            LandingHeroSection(onGetStarted = ::openApp, onSignIn = ::openApp)

            LandingTrustStrip()

            LandingSection(
                eyebrow = "Why SpendTel",
                title = "Your statement already has the data — we make it impossible to ignore",
                subtitle = "SpendTel reads your transactions and surfaces personality, patterns, leaks, and goal-aware insights you won't find in a bank app or spreadsheet.",
            ) {
                LandingFeatureGrid()
            }

            LandingSection(
                eyebrow = "Only in SpendTel",
                title = "Insights that feel personal, not punitive",
                subtitle = "Real outputs from your reports — the kind of data that makes you stop scrolling and actually think about your money.",
            ) {
                LandingUniqueDataShowcase()
            }

            LandingSection(
                eyebrow = "How it works",
                title = "Three steps to your spending story",
                subtitle = "Upload PDF or CSV statements. Pick a financial goal. Get a narrative report in about two minutes — your first story is free.",
            ) {
                LandingStepsRow()
            }

            LandingSection(
                eyebrow = "See it in action",
                title = "A report built for sharing",
                subtitle = "Wrapped-style chapters, merchant intel, spending equivalences, and subscription leaks — all tied to the goal you care about.",
            ) {
                LandingReportPreview()
            }

            LandingTrustSection()

            LandingFinalCta(onGetStarted = ::openApp)

            LandingFooter(navHostController)
        }
    }
}

@Composable
private fun LandingTopNav(
    onSignIn: () -> Unit,
    onGetStarted: () -> Unit,
) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        color = Color.White.copy(alpha = 0.92f),
        shadowElevation = 1.dp,
    ) {
        BoxWithConstraints(
            modifier = Modifier.fillMaxWidth(),
            contentAlignment = Alignment.TopCenter,
        ) {
            val compact = maxWidth < webCompactBreakpoint
            Row(
                modifier = Modifier
                    .widthIn(max = pageMaxWidth)
                    .fillMaxWidth()
                    .padding(horizontal = sectionPadding, vertical = 16.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(
                    text = "SPENDTEL",
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    letterSpacing = 2.sp,
                    color = SpendTelReportColors.ink,
                )

                Row(
                    horizontalArrangement = Arrangement.spacedBy(if (compact) 4.dp else 8.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    TextButton(onClick = onSignIn) {
                        Text(
                            text = "Sign in",
                            color = SpendTelReportColors.inkMuted,
                            fontWeight = FontWeight.Medium,
                        )
                    }
                    Button(
                        onClick = onGetStarted,
                        shape = RoundedCornerShape(12.dp),
                        colors = ButtonDefaults.buttonColors(
                            containerColor = deepGreen,
                            contentColor = Color.White,
                        ),
                    ) {
                        Text(
                            text = if (compact) "Start free" else "Get started free",
                            fontWeight = FontWeight.SemiBold,
                            modifier = if (compact) Modifier.padding(horizontal = 4.dp) else Modifier,
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun LandingHeroSection(
    onGetStarted: () -> Unit,
    onSignIn: () -> Unit,
) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .background(SpendTelGradients.hero)
            .padding(bottom = 48.dp),
    ) {
        Box(
            modifier = Modifier
                .size(280.dp)
                .align(Alignment.TopEnd)
                .offset(x = 80.dp, y = (-40).dp)
                .clip(CircleShape)
                .background(Color.White.copy(alpha = 0.06f)),
        )
        Box(
            modifier = Modifier
                .size(160.dp)
                .align(Alignment.BottomStart)
                .offset(x = (-40).dp, y = 20.dp)
                .clip(CircleShape)
                .background(Color.White.copy(alpha = 0.05f)),
        )

        BoxWithConstraints(
            modifier = Modifier
                .widthIn(max = pageMaxWidth)
                .fillMaxWidth()
                .padding(horizontal = sectionPadding, vertical = 56.dp)
                .align(Alignment.Center),
        ) {
            val wideLayout = maxWidth >= 900.dp

            if (wideLayout) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(48.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    LandingHeroCopy(
                        modifier = Modifier.weight(1f),
                        onGetStarted = onGetStarted,
                        onSignIn = onSignIn,
                    )
                    LandingHeroPreview(modifier = Modifier.weight(1f))
                }
            } else {
                Column(verticalArrangement = Arrangement.spacedBy(32.dp)) {
                    LandingHeroCopy(
                        modifier = Modifier.fillMaxWidth(),
                        onGetStarted = onGetStarted,
                        onSignIn = onSignIn,
                    )
                    LandingHeroPreview(modifier = Modifier.fillMaxWidth())
                }
            }
        }
    }
}

@Composable
private fun LandingHeroCopy(
    modifier: Modifier,
    onGetStarted: () -> Unit,
    onSignIn: () -> Unit,
) {
    Column(
        modifier = modifier,
        verticalArrangement = Arrangement.spacedBy(20.dp),
    ) {
        Surface(
            shape = RoundedCornerShape(999.dp),
            color = Color.White.copy(alpha = 0.12f),
        ) {
            Text(
                text = "First spending story free",
                modifier = Modifier.padding(horizontal = 14.dp, vertical = 8.dp),
                style = MaterialTheme.typography.labelMedium,
                color = Color.White.copy(alpha = 0.9f),
            )
        }

        Text(
            text = "Your bank shows transactions. SpendTel shows who you are with money.",
            style = MaterialTheme.typography.displaySmall,
            fontWeight = FontWeight.Bold,
            color = Color.White,
            lineHeight = 44.sp,
        )

        Text(
            text = "Upload statements and unwrap a narrative report: financial personality, spending equivalences, merchant rhythms, money leaks, and insights tailored to your savings goal.",
            style = MaterialTheme.typography.bodyLarge,
            color = Color.White.copy(alpha = 0.88f),
            lineHeight = 28.sp,
        )

        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(12.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Button(
                onClick = onGetStarted,
                modifier = Modifier
                    .weight(1f)
                    .height(52.dp),
                shape = RoundedCornerShape(14.dp),
                colors = ButtonDefaults.buttonColors(
                    containerColor = Color.White,
                    contentColor = SpendTelReportColors.ink,
                ),
            ) {
                Text(
                    text = "Try SpendTel free",
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier.padding(horizontal = 8.dp),
                    maxLines = 1,
                )
            }
            OutlinedButton(
                onClick = onSignIn,
                modifier = Modifier
                    .weight(1f)
                    .height(52.dp),
                shape = RoundedCornerShape(14.dp),
                colors = ButtonDefaults.outlinedButtonColors(
                    contentColor = Color.White,
                ),
                border = ButtonDefaults.outlinedButtonBorder.copy(
                    brush = Brush.linearGradient(listOf(Color.White.copy(alpha = 0.6f), Color.White.copy(alpha = 0.6f))),
                ),
            ) {
                Text(
                    text = "Sign in",
                    fontWeight = FontWeight.SemiBold,
                    modifier = Modifier.padding(horizontal = 8.dp),
                )
            }
        }

        Text(
            text = "First report free · Goal-aware insights · Encrypted uploads",
            style = MaterialTheme.typography.bodySmall,
            color = Color.White.copy(alpha = 0.72f),
        )
    }
}

@Composable
private fun LandingHeroPreview(modifier: Modifier = Modifier) {
    BoxWithConstraints(modifier = modifier, contentAlignment = Alignment.Center) {
        val compact = maxWidth < webCompactBreakpoint
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(if (compact) 440.dp else 480.dp)
                .clip(RoundedCornerShape(28.dp))
                .background(
                    Brush.linearGradient(
                        colors = listOf(
                            Color(0xFF2A1F4E),
                            Color(0xFF1B3558),
                        ),
                    ),
                )
                .border(1.dp, Color.White.copy(alpha = 0.12f), RoundedCornerShape(28.dp))
                .padding(20.dp),
        ) {
            Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
                if (compact) {
                    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                        Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                            Text(
                                text = "March spending story",
                                color = Color.White,
                                fontWeight = FontWeight.SemiBold,
                            )
                            Surface(
                                shape = RoundedCornerShape(999.dp),
                                color = SpendTelReportColors.mint.copy(alpha = 0.25f),
                            ) {
                                Text(
                                    text = "Goal: Save more",
                                    modifier = Modifier.padding(horizontal = 10.dp, vertical = 4.dp),
                                    style = MaterialTheme.typography.labelSmall,
                                    color = Color.White,
                                )
                            }
                        }
                        Surface(
                            shape = RoundedCornerShape(8.dp),
                            color = SpendTelReportColors.violet.copy(alpha = 0.35f),
                        ) {
                            Text(
                                text = "The Optimistic Explorer",
                                modifier = Modifier.padding(horizontal = 10.dp, vertical = 4.dp),
                                style = MaterialTheme.typography.labelSmall,
                                color = Color.White,
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis,
                            )
                        }
                    }
                } else {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                            Text(
                                text = "March spending story",
                                color = Color.White,
                                fontWeight = FontWeight.SemiBold,
                            )
                            Surface(
                                shape = RoundedCornerShape(999.dp),
                                color = SpendTelReportColors.mint.copy(alpha = 0.25f),
                            ) {
                                Text(
                                    text = "Goal: Save more",
                                    modifier = Modifier.padding(horizontal = 10.dp, vertical = 4.dp),
                                    style = MaterialTheme.typography.labelSmall,
                                    color = Color.White,
                                )
                            }
                        }
                        Surface(
                            shape = RoundedCornerShape(8.dp),
                            color = SpendTelReportColors.violet.copy(alpha = 0.35f),
                        ) {
                            Text(
                                text = "The Optimistic Explorer",
                                modifier = Modifier.padding(horizontal = 10.dp, vertical = 4.dp),
                                style = MaterialTheme.typography.labelSmall,
                                color = Color.White,
                            )
                        }
                    }
                }

                Surface(
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(18.dp),
                    color = Color.White.copy(alpha = 0.08f),
                ) {
                    Column(
                        modifier = Modifier.padding(16.dp),
                        verticalArrangement = Arrangement.spacedBy(10.dp),
                    ) {
                        Text(
                            text = "Comfort spending climbed — but your savings rate quietly improved",
                            color = Color.White,
                            fontWeight = FontWeight.Bold,
                            fontSize = 18.sp,
                            lineHeight = 24.sp,
                        )
                        Text(
                            text = "Coffee shops and rideshare lead the charge. Whole Foods visits are up 3× vs last month.",
                            color = Color.White.copy(alpha = 0.82f),
                            lineHeight = 22.sp,
                        )
                    }
                }

                Surface(
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(14.dp),
                    color = SpendTelReportColors.amber.copy(alpha = 0.18f),
                ) {
                    Row(
                        modifier = Modifier.padding(14.dp),
                        horizontalArrangement = Arrangement.spacedBy(10.dp),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Icon(
                            imageVector = Icons.Outlined.CompareArrows,
                            contentDescription = null,
                            tint = SpendTelReportColors.amber,
                            modifier = Modifier.size(20.dp),
                        )
                        Column {
                            Text(
                                text = "$842 on dining ≈ 168 lattes",
                                color = Color.White,
                                fontWeight = FontWeight.SemiBold,
                                fontSize = 14.sp,
                            )
                            Text(
                                text = "Spending equivalence",
                                color = Color.White.copy(alpha = 0.7f),
                                style = MaterialTheme.typography.labelSmall,
                            )
                        }
                    }
                }

                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(IntrinsicSize.Max),
                    horizontalArrangement = Arrangement.spacedBy(10.dp),
                ) {
                    LandingPreviewStat(
                        label = "Money leaks",
                        value = "$47/mo subs",
                        accent = SpendTelReportColors.rose,
                        modifier = Modifier
                            .weight(1f)
                            .fillMaxHeight(),
                    )
                    LandingPreviewStat(
                        label = "vs last story",
                        value = "+12% dining",
                        accent = SpendTelReportColors.teal,
                        modifier = Modifier
                            .weight(1f)
                            .fillMaxHeight(),
                    )
                }

                LandingMiniChart()

                LandingTagRow(
                    tags = listOf("Uber · 14 visits", "Spotify · $12/mo", "Weekend spike"),
                    textColor = Color.White.copy(alpha = 0.85f),
                    backgroundColor = Color.White.copy(alpha = 0.1f),
                )
            }
        }
    }
}

@Composable
private fun LandingPreviewStat(
    label: String,
    value: String,
    accent: Color,
    modifier: Modifier = Modifier,
) {
    Surface(
        modifier = modifier,
        shape = RoundedCornerShape(14.dp),
        color = Color.White.copy(alpha = 0.07f),
    ) {
        Column(
            modifier = Modifier
                .fillMaxHeight()
                .padding(12.dp),
        ) {
            Box(
                modifier = Modifier
                    .size(8.dp)
                    .clip(CircleShape)
                    .background(accent),
            )
            Spacer(modifier = Modifier.height(8.dp))
            Text(
                text = label,
                style = MaterialTheme.typography.labelSmall,
                color = Color.White.copy(alpha = 0.65f),
            )
            Text(
                text = value,
                fontWeight = FontWeight.Bold,
                color = Color.White,
            )
        }
    }
}

@Composable
private fun LandingMiniChart() {
    val bars = listOf(0.45f, 0.72f, 0.55f, 0.88f, 0.62f, 0.95f)
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .height(72.dp)
            .clip(RoundedCornerShape(12.dp))
            .background(Color.White.copy(alpha = 0.06f))
            .padding(horizontal = 12.dp, vertical = 10.dp),
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        verticalAlignment = Alignment.Bottom,
    ) {
        bars.forEachIndexed { index, heightFraction ->
            Box(
                modifier = Modifier
                    .weight(1f)
                    .fillMaxHeight(heightFraction)
                    .clip(RoundedCornerShape(topStart = 6.dp, topEnd = 6.dp))
                    .background(
                        Brush.verticalGradient(
                            colors = listOf(
                                SpendTelReportColors.sectionAccents[index % SpendTelReportColors.sectionAccents.size],
                                SpendTelReportColors.sectionAccents[index % SpendTelReportColors.sectionAccents.size]
                                    .copy(alpha = 0.55f),
                            ),
                        ),
                    ),
            )
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun LandingTagRow(
    tags: List<String>,
    textColor: Color,
    backgroundColor: Color,
    modifier: Modifier = Modifier,
) {
    FlowRow(
        modifier = modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        tags.forEach { tag ->
            Surface(
                shape = RoundedCornerShape(999.dp),
                color = backgroundColor,
            ) {
                Text(
                    text = tag,
                    modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp),
                    style = MaterialTheme.typography.labelSmall,
                    color = textColor,
                    maxLines = 1,
                    softWrap = false,
                    overflow = TextOverflow.Ellipsis,
                )
            }
        }
    }
}

@Composable
private fun LandingTrustStrip() {
    val items = listOf(
        "Financial personality",
        "Spending equivalences",
        "Money leak detection",
        "Goal-aware insights",
    )

    BoxWithConstraints(
        modifier = Modifier.fillMaxWidth(),
        contentAlignment = Alignment.TopCenter,
    ) {
        val compact = maxWidth < webCompactBreakpoint

        if (compact) {
            Column(
                modifier = Modifier
                    .widthIn(max = pageMaxWidth)
                    .fillMaxWidth()
                    .padding(horizontal = sectionPadding, vertical = 28.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp),
            ) {
                items.chunked(2).forEach { rowItems ->
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(12.dp),
                    ) {
                        rowItems.forEach { item ->
                            LandingTrustStripChip(
                                text = item,
                                modifier = Modifier.weight(1f),
                            )
                        }
                        if (rowItems.size == 1) {
                            Spacer(modifier = Modifier.weight(1f))
                        }
                    }
                }
            }
        } else {
            Row(
                modifier = Modifier
                    .widthIn(max = pageMaxWidth)
                    .fillMaxWidth()
                    .padding(horizontal = sectionPadding, vertical = 28.dp),
                horizontalArrangement = Arrangement.SpaceEvenly,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                items.forEach { item ->
                    Text(
                        text = item,
                        style = MaterialTheme.typography.labelLarge,
                        color = SpendTelReportColors.inkMuted,
                        fontWeight = FontWeight.Medium,
                    )
                }
            }
        }
    }
}

@Composable
private fun LandingTrustStripChip(
    text: String,
    modifier: Modifier = Modifier,
) {
    Surface(
        modifier = modifier,
        shape = RoundedCornerShape(12.dp),
        color = SpendTelReportColors.violet.copy(alpha = 0.06f),
    ) {
        Text(
            text = text,
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 12.dp, vertical = 12.dp),
            style = MaterialTheme.typography.labelMedium,
            color = SpendTelReportColors.inkMuted,
            fontWeight = FontWeight.Medium,
            textAlign = TextAlign.Center,
            lineHeight = 18.sp,
        )
    }
}

@Composable
private fun LandingSection(
    eyebrow: String,
    title: String,
    subtitle: String,
    content: @Composable () -> Unit,
) {
    Box(
        modifier = Modifier.fillMaxWidth(),
        contentAlignment = Alignment.TopCenter,
    ) {
        Column(
            modifier = Modifier
                .widthIn(max = pageMaxWidth)
                .fillMaxWidth()
                .padding(horizontal = sectionPadding, vertical = 40.dp),
            verticalArrangement = Arrangement.spacedBy(28.dp),
        ) {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Text(
                text = eyebrow.uppercase(),
                style = MaterialTheme.typography.labelMedium,
                color = SpendTelReportColors.violet,
                letterSpacing = 1.5.sp,
                fontWeight = FontWeight.SemiBold,
            )
            Text(
                text = title,
                style = MaterialTheme.typography.headlineMedium,
                fontWeight = FontWeight.Bold,
                color = SpendTelReportColors.ink,
                lineHeight = 36.sp,
            )
            Text(
                text = subtitle,
                style = MaterialTheme.typography.bodyLarge,
                color = SpendTelReportColors.inkMuted,
                lineHeight = 26.sp,
            )
        }
        content()
        }
    }
}

@Composable
private fun LandingUniqueDataShowcase() {
    val samples = listOf(
        LandingDataSample(
            eyebrow = "Spending equivalence",
            headline = "$842 on dining",
            body = "That's roughly 168 lattes — or a round-trip flight you kept putting off.",
            accent = SpendTelReportColors.amber,
            icon = Icons.Outlined.CompareArrows,
        ),
        LandingDataSample(
            eyebrow = "Money leaks",
            headline = "$47/mo in subscriptions",
            body = "Spotify, iCloud, and a gym membership you haven't used since January. Annualized: $564.",
            accent = SpendTelReportColors.rose,
            icon = Icons.Outlined.ReceiptLong,
        ),
        LandingDataSample(
            eyebrow = "Goal insight",
            headline = "Saving more? Start here.",
            body = "Your weekend convenience spending is 2.4× weekdays. Shifting two rides could fund your goal faster.",
            accent = SpendTelReportColors.mint,
            icon = Icons.Outlined.Flag,
        ),
        LandingDataSample(
            eyebrow = "Merchant rhythm",
            headline = "Uber · 14 visits",
            body = "Late-night rides cluster on Fridays. Average $18/trip — up 22% from your last story.",
            accent = SpendTelReportColors.indigo,
            icon = Icons.Outlined.Storefront,
        ),
    )

    BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
        if (maxWidth >= 900.dp) {
            Row(
                modifier = Modifier.height(IntrinsicSize.Max),
                horizontalArrangement = Arrangement.spacedBy(16.dp),
            ) {
                samples.forEach { sample ->
                    LandingDataSampleCard(
                        sample,
                        Modifier
                            .weight(1f)
                            .fillMaxHeight(),
                    )
                }
            }
        } else {
            Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                samples.forEach { sample ->
                    LandingDataSampleCard(sample, Modifier.fillMaxWidth())
                }
            }
        }
    }
}

private data class LandingDataSample(
    val eyebrow: String,
    val headline: String,
    val body: String,
    val accent: Color,
    val icon: ImageVector,
)

@Composable
private fun LandingDataSampleCard(
    sample: LandingDataSample,
    modifier: Modifier = Modifier,
) {
    Surface(
        modifier = modifier,
        shape = RoundedCornerShape(20.dp),
        color = sample.accent.copy(alpha = 0.08f),
        border = BorderStroke(1.dp, sample.accent.copy(alpha = 0.18f)),
    ) {
        Column(
            modifier = Modifier
                .fillMaxHeight()
                .padding(20.dp),
            verticalArrangement = Arrangement.spacedBy(10.dp),
        ) {
            Row(
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Icon(
                    imageVector = sample.icon,
                    contentDescription = null,
                    tint = sample.accent,
                    modifier = Modifier.size(18.dp),
                )
                Text(
                    text = sample.eyebrow.uppercase(),
                    style = MaterialTheme.typography.labelSmall,
                    color = sample.accent,
                    fontWeight = FontWeight.Bold,
                    letterSpacing = 1.sp,
                )
            }
            Text(
                text = sample.headline,
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                color = SpendTelReportColors.ink,
            )
            Text(
                text = sample.body,
                style = MaterialTheme.typography.bodyMedium,
                color = SpendTelReportColors.inkMuted,
                lineHeight = 22.sp,
            )
        }
    }
}

@Composable
private fun LandingFeatureGrid() {
    BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
        val columns = if (maxWidth >= 800.dp) 3 else 1
        val features = listOf(
            Triple(Icons.Outlined.Psychology, "Financial personality", "Get a named spending archetype and narrative — not just pie charts."),
            Triple(Icons.Outlined.CompareArrows, "Spending equivalences", "See \"$842 on dining\" as lattes, flights, or hours worked — instantly visceral."),
            Triple(Icons.Outlined.ReceiptLong, "Money leaks", "We surface recurring subscriptions, fees, and annualized costs hiding in plain sight."),
            Triple(Icons.Outlined.Flag, "Goal-aware insights", "Pick a goal — save more, pay off debt, stick to a budget — and get advice tied to it."),
            Triple(Icons.Outlined.Storefront, "Merchant intel", "Visit counts, spend patterns, and trends for the places that define your month."),
            Triple(Icons.Outlined.ShowChart, "Story-to-story trends", "Track personality shifts, category changes, and new habits since your last report."),
        )

        if (columns == 3) {
            Column(verticalArrangement = Arrangement.spacedBy(20.dp)) {
                features.chunked(3).forEach { row ->
                    Row(
                        modifier = Modifier.height(IntrinsicSize.Max),
                        horizontalArrangement = Arrangement.spacedBy(20.dp),
                    ) {
                        row.forEach { (icon, title, body) ->
                            LandingFeatureCard(
                                icon,
                                title,
                                body,
                                Modifier
                                    .weight(1f)
                                    .fillMaxHeight(),
                            )
                        }
                        repeat(3 - row.size) {
                            Spacer(modifier = Modifier.weight(1f))
                        }
                    }
                }
            }
        } else {
            Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                features.forEach { (icon, title, body) ->
                    LandingFeatureCard(icon, title, body, Modifier.fillMaxWidth())
                }
            }
        }
    }
}

@Composable
private fun LandingFeatureCard(
    icon: ImageVector,
    title: String,
    body: String,
    modifier: Modifier = Modifier,
) {
    Card(
        modifier = modifier,
        shape = RoundedCornerShape(20.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp),
    ) {
        Column(
            modifier = Modifier
                .fillMaxHeight()
                .padding(22.dp),
            verticalArrangement = Arrangement.spacedBy(14.dp),
        ) {
            Surface(
                shape = RoundedCornerShape(14.dp),
                color = SpendTelReportColors.violet.copy(alpha = 0.12f),
            ) {
                Icon(
                    imageVector = icon,
                    contentDescription = null,
                    tint = SpendTelReportColors.violet,
                    modifier = Modifier.padding(12.dp),
                )
            }
            Text(
                text = title,
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                color = SpendTelReportColors.ink,
            )
            Text(
                text = body,
                style = MaterialTheme.typography.bodyMedium,
                color = SpendTelReportColors.inkMuted,
                lineHeight = 22.sp,
            )
        }
    }
}

@Composable
private fun LandingStepsRow() {
    val steps = listOf(
        Triple(Icons.Outlined.UploadFile, "Upload", "Drop in PDF or CSV statements from your bank or card issuer."),
        Triple(Icons.Outlined.Flag, "Set your goal", "Tell us what you're working toward — save more, pay off debt, or understand habits."),
        Triple(Icons.Outlined.AutoAwesome, "Unwrap", "Read your personality, equivalences, leaks, and merchant intel in minutes."),
    )

    BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
        if (maxWidth >= 800.dp) {
            Row(
                modifier = Modifier.height(IntrinsicSize.Max),
                horizontalArrangement = Arrangement.spacedBy(20.dp),
            ) {
                steps.forEachIndexed { index, step ->
                    LandingStepCard(
                        stepNumber = index + 1,
                        icon = step.first,
                        title = step.second,
                        body = step.third,
                        modifier = Modifier
                            .weight(1f)
                            .fillMaxHeight(),
                    )
                }
            }
        } else {
            Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                steps.forEachIndexed { index, step ->
                    LandingStepCard(
                        stepNumber = index + 1,
                        icon = step.first,
                        title = step.second,
                        body = step.third,
                        modifier = Modifier.fillMaxWidth(),
                    )
                }
            }
        }
    }
}

@Composable
private fun LandingStepCard(
    stepNumber: Int,
    icon: ImageVector,
    title: String,
    body: String,
    modifier: Modifier = Modifier,
) {
    Surface(
        modifier = modifier,
        shape = RoundedCornerShape(20.dp),
        color = SpendTelReportColors.canvasDark,
    ) {
        Column(
            modifier = Modifier
                .fillMaxHeight()
                .padding(22.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Surface(
                    shape = CircleShape,
                    color = deepGreen,
                ) {
                    Text(
                        text = stepNumber.toString(),
                        modifier = Modifier.padding(horizontal = 11.dp, vertical = 6.dp),
                        color = Color.White,
                        fontWeight = FontWeight.Bold,
                    )
                }
                Spacer(modifier = Modifier.width(12.dp))
                Icon(
                    imageVector = icon,
                    contentDescription = null,
                    tint = SpendTelReportColors.ink,
                )
            }
            Text(
                text = title,
                fontWeight = FontWeight.Bold,
                color = SpendTelReportColors.ink,
            )
            Text(
                text = body,
                color = SpendTelReportColors.inkMuted,
                lineHeight = 22.sp,
            )
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun LandingReportPreview() {
    BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
        val compact = maxWidth < webCompactBreakpoint
        val categoryStats = listOf(
            "Dining" to SpendTelReportColors.coral,
            "Subscriptions" to SpendTelReportColors.rose,
            "Travel" to SpendTelReportColors.indigo,
        )

        Card(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(24.dp),
            colors = CardDefaults.cardColors(containerColor = Color.White),
            elevation = CardDefaults.cardElevation(defaultElevation = 4.dp),
        ) {
            Column(
                modifier = Modifier.padding(if (compact) 20.dp else 24.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp),
            ) {
                if (compact) {
                    Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                        Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                            FlowRow(
                                horizontalArrangement = Arrangement.spacedBy(8.dp),
                                verticalArrangement = Arrangement.spacedBy(8.dp),
                            ) {
                                Text(
                                    text = "Your March wrapped",
                                    fontWeight = FontWeight.Bold,
                                    color = SpendTelReportColors.ink,
                                    fontSize = 22.sp,
                                )
                                Surface(
                                    shape = RoundedCornerShape(999.dp),
                                    color = SpendTelReportColors.violet.copy(alpha = 0.12f),
                                ) {
                                    Text(
                                        text = "LATEST",
                                        modifier = Modifier.padding(horizontal = 8.dp, vertical = 3.dp),
                                        style = MaterialTheme.typography.labelSmall,
                                        color = SpendTelReportColors.violet,
                                        fontWeight = FontWeight.Bold,
                                        maxLines = 1,
                                        softWrap = false,
                                    )
                                }
                            }
                            Text(
                                text = "The Optimistic Explorer · Goal: Save more",
                                color = SpendTelReportColors.inkMuted,
                                fontWeight = FontWeight.Medium,
                            )
                            Text(
                                text = "2 statements · 847 transactions",
                                color = SpendTelReportColors.inkMuted,
                                style = MaterialTheme.typography.bodySmall,
                            )
                        }
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .clip(RoundedCornerShape(12.dp))
                                .background(SpendTelGradients.personality),
                            contentAlignment = Alignment.Center,
                        ) {
                            Text(
                                text = "Share",
                                modifier = Modifier.padding(horizontal = 16.dp, vertical = 10.dp),
                                color = Color.White,
                                fontWeight = FontWeight.SemiBold,
                            )
                        }
                    }
                } else {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.Top,
                    ) {
                        Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                            Row(
                                horizontalArrangement = Arrangement.spacedBy(8.dp),
                                verticalAlignment = Alignment.CenterVertically,
                            ) {
                                Text(
                                    text = "Your March wrapped",
                                    fontWeight = FontWeight.Bold,
                                    color = SpendTelReportColors.ink,
                                    fontSize = 22.sp,
                                )
                                Surface(
                                    shape = RoundedCornerShape(999.dp),
                                    color = SpendTelReportColors.violet.copy(alpha = 0.12f),
                                ) {
                                    Text(
                                        text = "LATEST",
                                        modifier = Modifier.padding(horizontal = 8.dp, vertical = 3.dp),
                                        style = MaterialTheme.typography.labelSmall,
                                        color = SpendTelReportColors.violet,
                                        fontWeight = FontWeight.Bold,
                                    )
                                }
                            }
                            Text(
                                text = "The Optimistic Explorer · Goal: Save more",
                                color = SpendTelReportColors.inkMuted,
                                fontWeight = FontWeight.Medium,
                            )
                            Text(
                                text = "2 statements · 847 transactions",
                                color = SpendTelReportColors.inkMuted,
                                style = MaterialTheme.typography.bodySmall,
                            )
                        }
                        Box(
                            modifier = Modifier
                                .clip(RoundedCornerShape(12.dp))
                                .background(SpendTelGradients.personality),
                        ) {
                            Text(
                                text = "Share",
                                modifier = Modifier.padding(horizontal = 16.dp, vertical = 10.dp),
                                color = Color.White,
                                fontWeight = FontWeight.SemiBold,
                            )
                        }
                    }
                }

                Surface(
                    shape = RoundedCornerShape(16.dp),
                    color = SpendTelReportColors.amber.copy(alpha = 0.12f),
                ) {
                    Row(
                        modifier = Modifier.padding(14.dp),
                        horizontalArrangement = Arrangement.spacedBy(10.dp),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Icon(
                            imageVector = Icons.Outlined.CompareArrows,
                            contentDescription = null,
                            tint = SpendTelReportColors.amber,
                        )
                        Column {
                            Text(
                                text = "$842 dining ≈ 168 lattes",
                                fontWeight = FontWeight.Bold,
                                color = SpendTelReportColors.ink,
                            )
                            Text(
                                text = "Tap to flip through more equivalences",
                                style = MaterialTheme.typography.bodySmall,
                                color = SpendTelReportColors.inkMuted,
                            )
                        }
                    }
                }

                if (compact) {
                    Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                        categoryStats.forEach { (label, color) ->
                            LandingReportCategoryStat(
                                label = label,
                                color = color,
                                modifier = Modifier.fillMaxWidth(),
                            )
                        }
                    }
                } else {
                    Row(
                        modifier = Modifier.height(IntrinsicSize.Max),
                        horizontalArrangement = Arrangement.spacedBy(12.dp),
                    ) {
                        categoryStats.forEach { (label, color) ->
                            LandingReportCategoryStat(
                                label = label,
                                color = color,
                                modifier = Modifier
                                    .weight(1f)
                                    .fillMaxHeight(),
                            )
                        }
                    }
                }

                Surface(
                    shape = RoundedCornerShape(18.dp),
                    color = SpendTelReportColors.canvas,
                ) {
                    Text(
                        text = "\"You're investing in comfort and connection — but three unused subscriptions are quietly costing you $564/year. Shift two Friday rides and you're closer to your savings goal.\"",
                        modifier = Modifier.padding(18.dp),
                        color = SpendTelReportColors.ink,
                        lineHeight = 24.sp,
                        fontStyle = androidx.compose.ui.text.font.FontStyle.Italic,
                    )
                }

                LandingTagRow(
                    tags = listOf("Weekend spike", "Personality shift", "3 new merchants"),
                    textColor = SpendTelReportColors.violet,
                    backgroundColor = SpendTelReportColors.violet.copy(alpha = 0.1f),
                )
            }
        }
    }
}

@Composable
private fun LandingReportCategoryStat(
    label: String,
    color: Color,
    modifier: Modifier = Modifier,
) {
    val value = when (label) {
        "Dining" -> "$842"
        "Subscriptions" -> "$47/mo"
        else -> "$1,240"
    }

    Surface(
        modifier = modifier,
        shape = RoundedCornerShape(16.dp),
        color = color.copy(alpha = 0.12f),
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(14.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                text = label,
                color = SpendTelReportColors.inkMuted,
                fontSize = 14.sp,
                maxLines = 1,
                softWrap = false,
            )
            Text(
                text = value,
                fontWeight = FontWeight.Bold,
                color = SpendTelReportColors.ink,
                fontSize = 20.sp,
                maxLines = 1,
                softWrap = false,
            )
        }
    }
}

@Composable
private fun LandingTrustSection() {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        color = SpendTelReportColors.canvasDark,
    ) {
        BoxWithConstraints(
            modifier = Modifier.fillMaxWidth(),
            contentAlignment = Alignment.TopCenter,
        ) {
            val compact = maxWidth < webCompactBreakpoint

            if (compact) {
                Column(
                    modifier = Modifier
                        .widthIn(max = pageMaxWidth)
                        .fillMaxWidth()
                        .padding(horizontal = sectionPadding, vertical = 40.dp),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(20.dp),
                ) {
                    Surface(
                        shape = CircleShape,
                        color = deepGreen.copy(alpha = 0.12f),
                    ) {
                        Icon(
                            imageVector = Icons.Outlined.Lock,
                            contentDescription = null,
                            tint = deepGreen,
                            modifier = Modifier.padding(18.dp),
                        )
                    }
                    Column(
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.spacedBy(6.dp),
                    ) {
                        Text(
                            text = "Private by design — delete anytime",
                            fontWeight = FontWeight.Bold,
                            color = SpendTelReportColors.ink,
                            fontSize = 20.sp,
                            textAlign = TextAlign.Center,
                        )
                        Text(
                            text = "Statements are encrypted in transit and at rest. Delete individual reports or your entire account — uploads included — from Settings.",
                            color = SpendTelReportColors.inkMuted,
                            lineHeight = 24.sp,
                            textAlign = TextAlign.Center,
                        )
                    }
                }
            } else {
                Row(
                    modifier = Modifier
                        .widthIn(max = pageMaxWidth)
                        .fillMaxWidth()
                        .padding(horizontal = sectionPadding, vertical = 48.dp),
                    horizontalArrangement = Arrangement.spacedBy(24.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Surface(
                        shape = CircleShape,
                        color = deepGreen.copy(alpha = 0.12f),
                    ) {
                        Icon(
                            imageVector = Icons.Outlined.Lock,
                            contentDescription = null,
                            tint = deepGreen,
                            modifier = Modifier.padding(18.dp),
                        )
                    }
                    Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                        Text(
                            text = "Private by design — delete anytime",
                            fontWeight = FontWeight.Bold,
                            color = SpendTelReportColors.ink,
                            fontSize = 20.sp,
                        )
                        Text(
                            text = "Statements are encrypted in transit and at rest. Delete individual reports or your entire account — uploads included — from Settings.",
                            color = SpendTelReportColors.inkMuted,
                            lineHeight = 24.sp,
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun LandingFinalCta(onGetStarted: () -> Unit) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .background(SpendTelGradients.hero)
            .padding(horizontal = sectionPadding, vertical = 64.dp),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            modifier = Modifier.widthIn(max = 640.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(20.dp),
        ) {
            Text(
                text = "Ready to see what your statement is really saying?",
                style = MaterialTheme.typography.headlineMedium,
                fontWeight = FontWeight.Bold,
                color = Color.White,
                textAlign = TextAlign.Center,
            )
            Text(
                text = "Your first spending story is free. Upload a statement, set your goal, and unwrap personality, equivalences, leaks, and merchant intel in minutes.",
                style = MaterialTheme.typography.bodyLarge,
                color = Color.White.copy(alpha = 0.88f),
                textAlign = TextAlign.Center,
                lineHeight = 26.sp,
            )
            Button(
                onClick = onGetStarted,
                modifier = Modifier.height(54.dp),
                shape = RoundedCornerShape(14.dp),
                colors = ButtonDefaults.buttonColors(
                    containerColor = Color.White,
                    contentColor = SpendTelReportColors.ink,
                ),
            ) {
                Text(
                    text = "Unwrap your first story — free",
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier.padding(horizontal = 12.dp),
                )
            }
        }
    }
}

@Composable
private fun LandingFooter(navHostController: NavHostController) {
    Box(
        modifier = Modifier.fillMaxWidth(),
        contentAlignment = Alignment.TopCenter,
    ) {
        Column(
            modifier = Modifier
                .widthIn(max = pageMaxWidth)
                .fillMaxWidth()
                .padding(bottom = 32.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Text(
                text = "© 2026 ${LegalConstants.COMPANY_NAME} · ${LegalConstants.PRODUCT_NAME}",
            style = MaterialTheme.typography.bodySmall,
            color = SpendTelReportColors.inkMuted,
            modifier = Modifier.padding(top = 16.dp, bottom = 8.dp),
        )
        LegalLinks(
            onPrivacyClick = {
                navHostController.navigate(AppRoutes.PrivacyPolicy.name)
            },
            onTermsClick = {
                navHostController.navigate(AppRoutes.TermsAndConditions.name)
            },
        )
        }
    }
}
